import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { Interactive } from "../src/interactions/Interactive";
import { useInteractionStore } from "../src/stores/useInteraction";

const PROXIMITY_RADIUS = 7;
const ROTATION_LERP = 0.2;
const CAMERA_OFFSET = { x: 4, y: 4, z: 0 };
const CAMERA_PITCH_OFFSET = THREE.MathUtils.degToRad(0);
const DIRECTION_EPSILON = 1e-5;

export default function SkeletonGreeter({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  targetRef = null,
  ...props
}) {
  const group = useRef();
  const [px = 0, py = 0, pz = 0] = position;
  const { scene, animations } = useGLTF("/interactive/skeleton0.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions } = useAnimations(animations, group);

  const actionNames = useMemo(() => Object.keys(actions || {}), [actions]);
  const baseEuler = useMemo(
    () => new THREE.Euler(rotation[0], rotation[1], rotation[2]),
    [rotation]
  );
  const skeletonWorldPos = useMemo(() => new THREE.Vector3(), []);
  const targetWorldPos = useMemo(() => new THREE.Vector3(), []);
  const lookDirection = useMemo(() => new THREE.Vector3(), []);
  const modelBounds = useMemo(() => new THREE.Box3(), []);
  const modelCenter = useMemo(() => new THREE.Vector3(), []);
  const cameraWorldPos = useMemo(() => new THREE.Vector3(), []);
  const cameraOverridePos = useMemo(() => new THREE.Vector3(), []);
  const cameraOffsetDirection = useMemo(() => new THREE.Vector3(), []);
  const rotatedDirection = useMemo(() => new THREE.Vector3(), []);
  const rightAxis = useMemo(() => new THREE.Vector3(), []);
  const worldUp = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const rotationQuaternion = useMemo(() => new THREE.Quaternion(), []);

  const camera = useThree((state) => state.camera);

  const lerpAngle = useCallback((current, target, alpha) => {
    let diff = ((target - current + Math.PI) % (Math.PI * 2)) - Math.PI;
    if (diff < -Math.PI) diff += Math.PI * 2;
    return current + diff * alpha;
  }, []);

  const interactive = useMemo(
    () =>
      new Interactive({
        id: "skeleton-greeter",
        promptText: "Hablar [E]",
        hudText: "Milaneso: jsjsjsjs ¿Cómo estás master? ¿Qué vas a querer?",
        radius: PROXIMITY_RADIUS,
        cameraConfig: {
          position: [
            px + CAMERA_OFFSET.x,
            py + CAMERA_OFFSET.y,
            pz + CAMERA_OFFSET.z,
          ],
          transitionDuration: 1,
        },
        getCameraTarget: () => {
          if (!group.current) return null;
          modelBounds.setFromObject(group.current);
          modelBounds.getCenter(modelCenter);
          cameraOverridePos.set(
            px + CAMERA_OFFSET.x,
            py + CAMERA_OFFSET.y,
            pz + CAMERA_OFFSET.z
          );

          cameraOffsetDirection.subVectors(modelCenter, cameraOverridePos);
          const distance = cameraOffsetDirection.length();

          if (distance <= DIRECTION_EPSILON) {
            return [modelCenter.x, modelCenter.y, modelCenter.z];
          }

          cameraOffsetDirection.normalize();
          rightAxis.crossVectors(cameraOffsetDirection, worldUp);

          if (rightAxis.lengthSq() <= DIRECTION_EPSILON) {
            rightAxis.set(1, 0, 0);
          } else {
            rightAxis.normalize();
          }

          rotationQuaternion.setFromAxisAngle(rightAxis, CAMERA_PITCH_OFFSET);

          rotatedDirection
            .copy(cameraOffsetDirection)
            .applyQuaternion(rotationQuaternion)
            .multiplyScalar(distance)
            .add(cameraOverridePos);

          return [rotatedDirection.x, rotatedDirection.y, rotatedDirection.z];
        },
        onInteract: () => {
          const {
            playerHidden,
            showShop,
            hideShop,
            shopVisible,
          } = useInteractionStore.getState();

          if (playerHidden) {
            if (!shopVisible) {
              showShop();
            }
            return;
          }

          if (shopVisible) {
            hideShop();
          }
        },
      }),
    [modelBounds, modelCenter, px, py, pz]
  );

  useEffect(() => () => interactive.dispose(), [interactive]);

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child.isMesh || child.isSkinnedMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [clonedScene]);

  useEffect(() => {
    if (group.current) {
      group.current.rotation.set(baseEuler.x, baseEuler.y, baseEuler.z);
    }
  }, [baseEuler]);

  useEffect(() => {
    if (!actions || actionNames.length === 0) return undefined;

    const [firstActionName] = actionNames;
    const loopAction = firstActionName ? actions[firstActionName] : undefined;
    if (!loopAction) return undefined;

    loopAction.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.15).play();
    loopAction.clampWhenFinished = false;

    return () => {
      loopAction.stop();
    };
  }, [actions, actionNames]);

  useFrame((_, delta) => {
    const skeletonGroup = group.current;
    if (!skeletonGroup) return;

    skeletonGroup.rotation.x = baseEuler.x;
    skeletonGroup.rotation.z = baseEuler.z;

    const targetGroup = targetRef?.current?.group;
    if (!targetGroup || typeof targetGroup.translation !== "function") {
      interactive.setWithinRadius(false);
      const alpha = Math.min(1, delta / ROTATION_LERP);
      skeletonGroup.rotation.y = lerpAngle(
        skeletonGroup.rotation.y,
        baseEuler.y,
        alpha
      );
      return;
    }

    const translation = targetGroup.translation();
    if (!translation) {
      interactive.setWithinRadius(false);
      const alpha = Math.min(1, delta / ROTATION_LERP);
      skeletonGroup.rotation.y = lerpAngle(
        skeletonGroup.rotation.y,
        baseEuler.y,
        alpha
      );
      return;
    }

    targetWorldPos.set(translation.x, translation.y, translation.z);
    skeletonGroup.getWorldPosition(skeletonWorldPos);

    const distance = skeletonWorldPos.distanceTo(targetWorldPos);
    interactive.setWithinRadius(distance <= interactive.radius, distance);

    lookDirection.subVectors(targetWorldPos, skeletonWorldPos);
    lookDirection.y = 0;

    if (lookDirection.lengthSq() <= 1e-5) {
      camera.getWorldPosition(cameraWorldPos);
      lookDirection.subVectors(cameraWorldPos, skeletonWorldPos);
      lookDirection.y = 0;
    }

    let desiredYaw = baseEuler.y;
    if (lookDirection.lengthSq() > 1e-5) {
      lookDirection.normalize();
      desiredYaw = Math.atan2(lookDirection.x, lookDirection.z);
    }

    const alpha = Math.min(1, delta / ROTATION_LERP);
    skeletonGroup.rotation.y = lerpAngle(
      skeletonGroup.rotation.y,
      desiredYaw,
      alpha
    );
  });

  return (
    <primitive
      ref={group}
      object={clonedScene}
      position={position}
      scale={scale}
      {...props}
    />
  );
}

useGLTF.preload("/interactive/skeleton0.glb");
